import { Box, Typography, type TypographyProps } from "@mui/material";
import type { GridColDef, GridColumnGroup, GridColumnNode, GridLeafColumn } from "@mui/x-data-grid-premium";
import type { JSX } from "react";
import { logError } from "@entrilia/shared/logging.ts";
import type {
  PivotColumnResponse,
  PivotRowDimensionCell,
  PivotRowResponse,
  PivotRowValueCell,
  ReportField,
} from "../../../../../../../api/types/svTypes.ts";
import DimensionStartIcon from "../common/DimensionStartIcon.tsx";
import { getFieldCaption, styleCaption } from "../../../utilities/Utilities.tsx";
import { DataGridTooltipWrapper } from "../../common/DataGridTooltipWrapper.tsx";
import GrandTotalCell from "../common/GrandTotalCell.tsx";
import { hasAvailableDrillDown } from "../common/hasAvailableDrillDown.ts";
import PivotExpansionButton, { type PivotExpansionControl } from "./PivotExpansionButton.tsx";

export const renderDisplayValue = (
  values: (string | undefined)[],
  toolTip: string | undefined,
  styleCallback: (value: string | undefined) => JSX.Element | string | undefined,
  typographyColor?: TypographyProps["color"],
  typographyVariant?: TypographyProps["variant"]
) => {
  if (values.length === 1) {
    return (
      <Typography
        sx={{ textDecoration: getTextDisplayValueDecoration(toolTip) }}
        color={typographyColor}
        variant={typographyVariant}
      >
        {styleCallback(values[0])}
      </Typography>
    );
  }
  return (
    <>
      <Typography
        color="textDisabled"
        sx={{ textDecoration: getTextDisplayValueDecoration(toolTip) }}
        variant={typographyVariant}
      >
        {styleCallback(values[0])}&nbsp;&nbsp;
      </Typography>
      <Typography
        sx={{ textDecoration: getTextDisplayValueDecoration(toolTip) }}
        color={typographyColor ? typographyColor : "textPrimary"}
        variant={typographyVariant}
      >
        {styleCallback(values[1])}
      </Typography>
    </>
  );
};

function getTextDisplayValueDecoration(toolTip: string | undefined) {
  return toolTip ? "underline dotted" : "none";
}

export function toGridColumn(column: PivotColumnResponse): GridColDef<PivotRowResponse> {
  return {
    field: column.id,
    align: column.id === "lines" ? "left" : "right",
    aggregable: false,
    disableColumnMenu: true,
    disableReorder: true,
    minWidth: 150,
    maxWidth: 500,
    description: column.toolTip,
    headerAlign: column.id === "lines" ? "left" : "right",
    sortable: column.id !== "lines",
    renderHeader: () => {
      if (column.id === "lines") {
        return null;
      }
      return createHeaderCell(column.formattedValues || [], column.toolTip);
    },
  } as GridColDef<PivotRowResponse>;
}

export function toGridColumnGroup(column: PivotColumnResponse): GridColumnGroup {
  return {
    groupId: column.id ?? "",
    headerAlign: column.id === "lines" ? "left" : "right",
    children: column.columns?.map(toGridColumnNode) || [],
    renderHeaderGroup: () => createHeaderCell(column.formattedValues || [], column.toolTip),
  };
}

function toGridColumnNode(column: PivotColumnResponse): GridColumnNode {
  if (column.measureGuid !== undefined) {
    const node: GridLeafColumn = { field: column.id ?? "" };

    return node;
  } else {
    return {
      groupId: column.id ?? "",
      headerAlign: "right",
      children: column.columns?.map(toGridColumnNode) || [],
      renderHeaderGroup: () => createHeaderCell(column.formattedValues || [], column.toolTip),
    };
  }
}

export function createMeasureCell(
  value: PivotRowValueCell | null | undefined,
  rows: ReportField[],
  disableCellClick: boolean
) {
  try {
    if (!value) {
      return null;
    }
    const isFieldLeaf = value.level === rows.length - 1;
    const rowField = rows[value.level];
    if (rowField) {
      return (
        <Box
          sx={(theme) => ({
            "&:hover":
              hasAvailableDrillDown(value?.drillDownId) && !disableCellClick
                ? { textDecoration: "underline", cursor: "pointer" }
                : {},
            color: theme.palette.text.primary,
          })}
        >
          {isFieldLeaf ? <Typography>{value.formattedValue}</Typography> : styleCaption(rowField, value.formattedValue)}
        </Box>
      );
    }
    return (
      <Box sx={(theme) => ({ color: theme.palette.text.primary })}>
        <Typography>{value.formattedValue}</Typography>
      </Box>
    );
  } catch (error) {
    logError(error, "render pivot measureCell");
    return null;
  }
}

export function createHeaderCell(formattedValues: string[], toolTip: string | undefined) {
  try {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          verticalAlign: "middle",
          justifyContent: "space-between",
        }}
      >
        <DataGridTooltipWrapper tooltip={toolTip}>
          {renderDisplayValue(formattedValues, toolTip, (value) => value, "textSecondary", "caption")}
        </DataGridTooltipWrapper>
      </Box>
    );
  } catch (error) {
    logError(error, "render pivot headerCell");
    return null;
  }
}

export function createLinesCell(
  cell: PivotRowDimensionCell | undefined,
  rows: ReportField[],
  expansion?: PivotExpansionControl
) {
  if (!cell) return null;
  try {
    const rowField = rows[cell.level];
    if (rowField) {
      const isFieldLeaf = cell.level === rows.length - 1;
      return (
        <DataGridTooltipWrapper tooltip={cell.toolTip}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: cell.level * 1.5 }}>
            <Box sx={{ width: 30, flexShrink: 0 }}>
              {expansion && (
                <PivotExpansionButton {...expansion} label={expansion.expanded ? "Collapse row" : "Expand row"} />
              )}
            </Box>
            {cell.formattedValues && <DimensionStartIcon cell={cell} />}
            {renderDisplayValue(cell.formattedValues, cell.toolTip, (value) =>
              !isFieldLeaf ? styleCaption(rowField, value) : value
            )}
          </Box>
        </DataGridTooltipWrapper>
      );
    }
    return (
      <DataGridTooltipWrapper tooltip={cell.toolTip}>
        <Box component={"div"} style={{ paddingLeft: 0 * 10 + 12 }} sx={{ display: "flex" }}>
          {renderDisplayValue(cell.formattedValues, cell.toolTip, (value) => value)}
        </Box>
      </DataGridTooltipWrapper>
    );
  } catch (error) {
    logError(error, "render pivot linesCell");
    return null;
  }
}

export function toGridColumnsForLines(rowFields: ReportField[]): GridColDef<PivotRowResponse>[] {
  return rowFields.map((rowField, level) => {
    const header = getFieldCaption(rowField);
    return {
      field: `lines_${level}`,
      align: "left",
      headerAlign: "left",
      sortable: false,
      aggregable: false,
      disableColumnMenu: true,
      disableReorder: true,
      minWidth: 150,
      maxWidth: 500,
      description: header,
      headerClassName: TABULAR_FORM_LINES_PLACEHOLDER_CLASS,
      renderHeader: () => createHeaderCell([header], undefined),
    } as GridColDef<PivotRowResponse>;
  });
}

export function createTabularFormLinesCell(
  cell: PivotRowDimensionCell | undefined,
  rowFields: ReportField[],
  level: number,
  isOwnLevel: boolean,
  reserveExpansionSpace: boolean,
  expansion?: PivotExpansionControl
) {
  if (!cell) return null;
  try {
    const rowField = rowFields[level];
    const isFieldLeaf = level === rowFields.length - 1;
    return (
      <DataGridTooltipWrapper tooltip={cell.toolTip}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {!isFieldLeaf && reserveExpansionSpace && (
            <Box sx={{ width: 30, flexShrink: 0 }}>
              {expansion && (
                <PivotExpansionButton {...expansion} label={expansion.expanded ? "Collapse row" : "Expand row"} />
              )}
            </Box>
          )}
          {isOwnLevel && cell.formattedValues && <DimensionStartIcon cell={cell} />}
          {renderDisplayValue(cell.formattedValues, cell.toolTip, (value) =>
            isOwnLevel && rowField && !isFieldLeaf ? styleCaption(rowField, value) : value
          )}
        </Box>
      </DataGridTooltipWrapper>
    );
  } catch (error) {
    logError(error, "render pivot tabular-form linesCell");
    return null;
  }
}

export const createGrandTotalLinesCell = () => <Typography variant="subtitle2">Grand Total</Typography>;

export const createGrandTotalMeasureCell = (value: PivotRowValueCell | null | undefined, disableCellClick: boolean) => (
  <GrandTotalCell
    formattedValue={value?.formattedValue ?? ""}
    disableCellClick={disableCellClick || !hasAvailableDrillDown(value?.drillDownId)}
  />
);

/** Max number of parent groups above any leaf measure in the pivot column tree. */
export function computeMaxColumnGroupDepth(columns: PivotColumnResponse[]): number {
  let max = 0;
  const walk = (col: PivotColumnResponse, depth: number) => {
    if (col.measureGuid !== undefined) {
      max = Math.max(max, depth);
    } else if (col.columns && col.columns.length > 0) {
      col.columns.forEach((c) => walk(c, depth + 1));
    }
  };
  columns.forEach((c) => walk(c, 0));
  return max;
}

export const TABULAR_FORM_LINES_PLACEHOLDER_CLASS = "pivot-tabular-form-lines-placeholder";

/** className applied to every placeholder empty header cell above a non-pivot measure column. */
export const NON_PIVOT_MEASURE_PLACEHOLDER_CLASS = "pivot-nonpivot-measure-placeholder";

/**
 * Wraps a measure leaf in N empty placeholder column groups with unique
 * groupIds, so MUI X renders a separate empty header cell per column at every
 * depth. Without this, adjacent ungrouped (non-pivot) measure columns in the
 * same container get merged into one big empty cell — no vertical separators
 * between them. Each placeholder cell carries `NON_PIVOT_MEASURE_PLACEHOLDER_CLASS`
 * as `headerClassName` so callers can target it for styling.
 */
export function wrapMeasureInPlaceholderGroups(field: string, levels: number): GridColumnGroup {
  let node: GridColumnNode = { field };
  for (let d = 0; d < levels; d++) {
    node = {
      groupId: `${field}_pad_${d}`,
      renderHeaderGroup: () => null,
      headerClassName: NON_PIVOT_MEASURE_PLACEHOLDER_CLASS,
      children: [node],
    };
  }
  return node as GridColumnGroup;
}

export const getDataColumns = (item: PivotColumnResponse): PivotColumnResponse[] => {
  if (item.measureGuid !== undefined) {
    return [item];
  } else if (item.columns.length > 0) {
    return item.columns.flatMap(getDataColumns);
  }
  return [];
};
